import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminSidebarLayout } from '../SidebarLayout';
import { Button } from '../ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Loader2, RefreshCw, MailCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { OverviewTab } from './OverviewTab';
import { QueueTab } from './QueueTab';
import { SuppressionsTab } from './SuppressionsTab';
import { SettingsTab } from './SettingsTab';
import { DailyBatchesPanel } from './DailyBatchesPanel';

export function AdminEmailHealthPage() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('overview');

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get('/api/admin/email/stats', { params: { days: 7 } });
      setStats(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load email stats');
    }
    setRefreshing(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-email-health-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white flex items-center gap-2">
              <MailCheck className="w-6 h-6 text-[#00E5FF]" />Email Health
            </h1>
            <p className="text-[#A0B4CC] text-sm mt-1">Queue, bounces, suppression list and delivery rules for all system emails.</p>
          </div>
          <Button onClick={load} variant="ghost" className="text-[#00E5FF]" disabled={refreshing} data-testid="email-health-refresh-btn">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}Refresh
          </Button>
        </div>

        {stats && stats.bounce_rate >= 5 && (
          <div className="mb-6 flex items-center gap-3 rounded-lg border border-[#FF5252]/30 bg-[#FF5252]/10 px-4 py-3 text-sm text-[#FFCDD2]" data-testid="bounce-rate-alert">
            <AlertTriangle className="w-5 h-5 text-[#FF5252]" />
            Bounce rate is {stats.bounce_rate}% — above the 5% level where providers start throttling. Review the suppression list.
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#041A3B] border border-white/10 mb-6 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="overview" data-testid="email-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="queue" data-testid="email-tab-queue">Queue</TabsTrigger>
            <TabsTrigger value="suppressions" data-testid="email-tab-suppressions">Suppressions & Bounces</TabsTrigger>
            <TabsTrigger value="settings" data-testid="email-tab-settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <DailyBatchesPanel />
            {stats ? <OverviewTab stats={stats} /> : <Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" />}
          </TabsContent>
          <TabsContent value="queue"><QueueTab onChanged={load} /></TabsContent>
          <TabsContent value="suppressions"><SuppressionsTab onChanged={load} /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
        </Tabs>
      </div>
    </AdminSidebarLayout>
  );
}
